// Filter state for the BKG search views.
//
// The view owns one flat map from each active leaf's path to the node that leaf
// contributes to the filter doc. The doc itself is derived from the schema plus
// that map, so it never depends on which parts of the filter tree are mounted.

import type { FilterInterface } from "./SearchFilters";
import { flattenFilterDoc } from "./filterHydration";

export type FilterValues = Record<string, FilterInterface>;

export type FilterBounds = Pick<FilterInterface, "min" | "max">;

export type LeafKind = "list" | "range" | "date";

// Which input a schema leaf renders as; null means it renders nothing.
export const leafKind = (filter: FilterInterface): LeafKind | null => {
  if (filter.allowed_values) return "list";
  if (filter.min === undefined || filter.max === undefined) return null;
  if (filter.type === "date") return "date";
  return filter.min !== filter.max ? "range" : null;
};

// Leaves before groups; the doc's child order follows this.
export const sortChildren = (
  children: FilterInterface[] = [],
): FilterInterface[] =>
  children
    .slice()
    .sort(
      (a, b) => Number(!!a.children?.length) - Number(!!b.children?.length),
    );

// Drop the keys first so their (possibly large) values are never cloned.
const without = (
  filter: FilterInterface,
  keys: Array<"allowed_values" | "contains" | "min" | "max" | "children">,
): FilterInterface => {
  const rest: Partial<FilterInterface> = { ...filter };
  for (const key of keys) delete rest[key];
  return structuredClone(rest) as FilterInterface;
};

// `contains` holds typed text the value must contain (name filters).
export const listNode = (
  filter: FilterInterface,
  selected: Array<string | number | boolean>,
  contains: string[] = [],
): FilterInterface | null =>
  selected.length === 0 && contains.length === 0
    ? null
    : {
        ...without(filter, ["allowed_values", "contains"]),
        allowed_values: selected,
        ...(contains.length ? { contains } : {}),
      };

export const boundsNode = (
  filter: FilterInterface,
  bounds: FilterBounds,
): FilterInterface => ({ ...without(filter, ["min", "max"]), ...bounds });

export const buildFilterDoc = (
  schema: FilterInterface,
  values: FilterValues,
): FilterInterface | null => {
  if (schema.children) {
    const children = sortChildren(schema.children)
      .map((child) => buildFilterDoc(child, values))
      .filter((child): child is FilterInterface => child !== null);
    return children.length
      ? { ...without(schema, ["children"]), children }
      : null;
  }
  return leafKind(schema) ? (values[schema.path] ?? null) : null;
};

// The leaves of a filter doc (e.g. from a ?f= link). Group nodes are derived.
export const filterValuesFromDoc = (doc: unknown): FilterValues => {
  const values: FilterValues = {};
  for (const [path, node] of Object.entries(flattenFilterDoc(doc))) {
    if (!node.children) values[path] = node;
  }
  return values;
};

// How many active leaves sit under each group; groups with none are absent.
export const activeGroupCounts = (
  schema: FilterInterface,
  values: FilterValues,
): Map<string, number> => {
  const counts = new Map<string, number>();
  const walk = (node: FilterInterface): number => {
    if (!node.children) {
      return leafKind(node) !== null && values[node.path] ? 1 : 0;
    }
    let total = 0;
    for (const child of node.children) total += walk(child);
    if (total > 0) counts.set(node.path, total);
    return total;
  };
  walk(schema);
  return counts;
};

// Active leaves across the whole schema; values for paths it lacks don't count.
export const activeFilterCount = (
  schema: FilterInterface,
  values: FilterValues,
): number => activeGroupCounts(schema, values).get(schema.path) ?? 0;
